import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../../../shared/api.js';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatIQD, formatDate } from '../../../shared/format.js';
import { useAuth } from '../../../shared/useAuth.js';
import { useShiftStatus } from '../../../shared/useShift.js';
import { ShiftDrawerNote } from '../../../shared/ShiftDrawerNote.jsx';
import { useSubscriptionPlans } from '../hooks/usePlans.js';
import { durationLabel } from './plans/PlanCard.jsx';
import { NewChildFields, validateNewChild } from './NewChildFields.jsx';
import { CardReadField, InitialBalanceField } from './CardReadField.jsx';

const METHODS = [
  { key: 'cash', label: 'نقدي' },
  { key: 'card', label: 'بطاقة' },
  { key: 'transfer', label: 'تحويل' }
];

function addDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + Math.max(1, Math.round(Number(days) || 0)));
  return d;
}

const EMPTY_DRAFT = {
  childName: '',
  guardianName: '',
  guardianPhone: '',
  childAge: '',
  childGender: 'male',
  photo: null,
  photoError: ''
};

/**
 * تسجيل اشتراك شهري: بحث بالاسم → طفل مسجَّل أو تسجيل طفل جديد → البطاقة
 * والرصيد الابتدائي → الباقة → الدفع.
 *
 * الطفل غير المسجَّل لا يحتاج جلسة سابقة: الخادم يُنشئ سجلّه وسجلّ ولي أمره
 * عبر خدمات وحدتَي الأطفال وأولياء الأمور، فيصير مستعملاً في كل الشاشات —
 * الاستقبال أولها. وإن كان الاسم مسجَّلاً أصلاً أُعيد استعمال سجلّه ولم
 * يُنشأ نسخة ثانية منه.
 */
export function SubscribeModal({ open, onClose, onSubmit, submitting }) {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canIssueCard = hasPermission('reception.issue_card');
  const canCreateChild = hasPermission('reception.create');
  const canReadCards = hasPermission('reception.view')
    || hasPermission('checkout.view')
    || hasPermission('inventory.view');

  const [term, setTerm] = useState('');
  const [child, setChild] = useState(null);
  // 'search' → نتائج البحث، 'new' → نموذج طفل جديد بعد موافقة الموظف
  const [mode, setMode] = useState('search');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [draftErrors, setDraftErrors] = useState({});
  const [cardUid, setCardUid] = useState('');
  const [card, setCard] = useState(null);
  const [initialBalance, setInitialBalance] = useState('');
  const [planId, setPlanId] = useState('');
  const [method, setMethod] = useState('cash');

  const { data: plans = [] } = useSubscriptionPlans({ active: true });
  const { shift, blocked } = useShiftStatus();

  useEffect(() => {
    if (!open) return;
    setTerm('');
    setChild(null);
    setMode('search');
    setDraft(EMPTY_DRAFT);
    setDraftErrors({});
    setCardUid('');
    setCard(null);
    setInitialBalance('');
    setMethod('cash');
    setPlanId('');
  }, [open]);

  useEffect(() => {
    if (!planId && plans.length) setPlanId(String(plans[0]._id || plans[0].id));
  }, [plans, planId]);

  const search = useQuery({
    queryKey: ['subscriptions', 'child-search', term],
    enabled: open && mode === 'search' && term.trim().length >= 2 && !child,
    queryFn: async () => {
      const { data } = await api.get('/children', { params: { q: term.trim(), active: true, limit: 8 } });
      return Array.isArray(data) ? data : (data?.items || []);
    }
  });

  // تغطية الطفل المختار — لمنع بيع اشتراك ثانٍ لمشترك فعّال (الخادم يرفضه أيضاً)
  const coverage = useQuery({
    queryKey: ['subscriptions', 'coverage', child ? String(child._id || child.id) : null],
    enabled: open && !!child,
    queryFn: async () => {
      const { data } = await api.get(`/subscriptions/child/${child._id || child.id}`);
      return data;
    }
  });

  const plan = useMemo(
    () => plans.find((p) => String(p._id || p.id) === String(planId)) || null,
    [plans, planId]
  );

  // بطاقة الطفل المسجَّل تُقرأ تلقائياً فلا يُعيد الموظف تمريرها
  useEffect(() => {
    if (child && child.cardUid && !cardUid) setCardUid(String(child.cardUid));
  }, [child]); // eslint-disable-line react-hooks/exhaustive-deps

  // الرصيد الابتدائي يبدأ من رصيد البطاقة الحالي (أو صفر) — وقابل للتعديل دائماً
  useEffect(() => {
    setInitialBalance(card ? String(Math.max(0, Math.round(Number(card.balance) || 0))) : '');
  }, [card ? String(card._id || card.uid) : null]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const results = search.data || [];
  const alreadyCovered = !!coverage.data?.covered;
  const currentBalance = Math.max(0, Math.round(Number(card?.balance) || 0));
  const targetBalance = initialBalance === '' || initialBalance == null
    ? currentBalance
    : Math.max(0, Math.round(Number(initialBalance) || 0));
  const balanceDecrease = !!card && targetBalance < currentBalance;

  const hasSubject = mode === 'new'
    ? String(draft.childName || '').trim().length >= 2
    : !!child;
  // رقم بطاقة مكتوب لم يُحَلّ بعد (قيد التحقق أو غير مسجّل) يمنع الحفظ: الخادم
  // سيرفضه، فالأفضل أن يرى الموظف السبب في الحقل لا في رسالة خطأ بعد الضغط.
  const cardPending = !!String(cardUid || '').trim() && !card;
  const canSubmit = hasSubject && !!plan && !alreadyCovered && !blocked
    && !balanceDecrease && !cardPending && !submitting;
  const blockReason = !hasSubject ? 'اختر الطفل أو اكتب اسمه'
    : !plan ? 'اختر باقة الاشتراك'
      : blocked ? 'افتح شفتاً أولاً'
        : alreadyCovered ? 'الطفل مشترك فعلاً — استخدم التجديد'
          : cardPending ? 'أكمل قراءة البطاقة أو أضفها، أو افرِغ الحقل'
            : balanceDecrease ? 'الرصيد الابتدائي أقل من رصيد البطاقة' : '';

  const startNewChild = () => {
    setMode('new');
    setChild(null);
    setDraft({ ...EMPTY_DRAFT, childName: term.trim() });
    setDraftErrors({});
  };

  const backToSearch = () => {
    setMode('search');
    setDraft(EMPTY_DRAFT);
    setDraftErrors({});
  };

  const submit = () => {
    const body = {
      planId: String(plan._id || plan.id),
      paymentMethod: method
    };
    if (mode === 'new') {
      const errors = validateNewChild(draft);
      setDraftErrors(errors);
      if (Object.keys(errors).length) return;
      body.childName = String(draft.childName).trim();
      body.guardianName = String(draft.guardianName).trim();
      body.guardianPhone = String(draft.guardianPhone).trim();
      body.childAge = Number(draft.childAge);
      body.childGender = draft.childGender || 'male';
      if (draft.photo) body.childPhoto = draft.photo;
    } else {
      body.childId = String(child._id || child.id);
    }
    const uid = String(cardUid || '').trim().toUpperCase();
    if (uid) {
      body.cardUid = uid;
      // البطاقة المقروءة والمعروفة تُربط كما هي؛ وغير المعروفة لا تُسجَّل إلا
      // إذا أضافها الموظف فعلاً من الحقل (فتصير معروفة) — فلا تسجيل بالسهو.
      body.registerCard = !!card;
      if (card) body.initialBalance = targetBalance;
    }
    onSubmit?.(body);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="card w-[580px] max-w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="card-h">
          <div className="flex-1">
            <h3 className="text-[15px] font-bold">اشتراك شهري جديد</h3>
            <div className="text-xs text-ink-3 mt-0.5">الاشتراك على الطفل — يبقى صالحاً لو تغيّر الكارت أو ضاع</div>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3">
          {blocked ? (
            <div
              className="rounded-[10px] px-3 py-2 text-[12.5px]"
              style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' }}
            >
              لا يوجد شفت مفتوح — افتح شفتاً أولاً، فبيع الاشتراك عملية نقدية تدخل درج الشفت.
            </div>
          ) : <ShiftDrawerNote shift={shift} />}

          {/* ── الطفل: بحث بالاسم، أو تسجيل طفل غير مسجّل */}
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الطفل *</label>

            {child ? (
              <div className="flex items-center justify-between gap-2 rounded-[10px] border border-line px-3 py-2 bg-paper-2">
                <div>
                  <div className="text-[13.5px] font-bold">{child.name}</div>
                  <div className="text-[11.5px] text-ink-3">
                    {child.age != null ? `${child.age} سنة` : ''}
                    {child.guardianId?.name ? ` • ${child.guardianId.name}` : ''}
                    {child.guardianId?.phone ? ` • ${child.guardianId.phone}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={() => { setChild(null); setMode('search'); }}
                >
                  تغيير
                </button>
              </div>
            ) : mode === 'new' ? (
              <div className="space-y-2">
                <NewChildFields draft={draft} onChange={setDraft} errors={draftErrors} />
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={backToSearch}
                >
                  <Icon name="search" size={13} /> رجوع للبحث عن طفل مسجَّل
                </button>
              </div>
            ) : (
              <>
                <input
                  className="input"
                  placeholder="ابحث باسم الطفل (حرفان على الأقل)"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                />
                {term.trim().length >= 2 ? (
                  <div className="mt-1.5 max-h-[180px] overflow-y-auto border border-line rounded-[10px] divide-y divide-line">
                    {search.isLoading ? (
                      <div className="text-[12px] text-ink-3 p-3">جاري البحث...</div>
                    ) : results.length === 0 ? (
                      <div className="p-3 space-y-2">
                        <div className="text-[12.5px]" style={{ color: '#92400E' }}>
                          هذا الطفل غير مسجل في النظام، هل ترغب بإضافة بياناته وإنشاء اشتراك شهري له؟
                        </div>
                        {canCreateChild ? (
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={startNewChild}
                          >
                            <Icon name="plus" size={13} /> نعم، أضف بياناته
                          </button>
                        ) : (
                          <div className="text-[11.5px] text-ink-3">
                            إضافة طفل جديد تحتاج صلاحية «إضافة ولي أمر/طفل»
                          </div>
                        )}
                      </div>
                    ) : (
                      results.map((c) => (
                        <button
                          key={c._id || c.id}
                          type="button"
                          className="w-full text-right px-3 py-2 hover:bg-paper-2"
                          onClick={() => setChild(c)}
                        >
                          <div className="text-[13px] font-semibold">{c.name}</div>
                          <div className="text-[11px] text-ink-3">
                            {c.age != null ? `${c.age} سنة` : ''}
                            {c.guardianId?.phone ? ` • ${c.guardianId.phone}` : ''}
                            {c.cardUid ? ` • بطاقة ${c.cardUid}` : ''}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                ) : null}
              </>
            )}
          </div>

          {alreadyCovered ? (
            <div
              className="rounded-[10px] px-3 py-2 text-[12.5px]"
              style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}
            >
              هذا الطفل لديه اشتراك فعّال حتى {formatDate(coverage.data.subscription?.endDate)} —
              استخدم «تجديد» من قائمة المشتركين بدلاً من اشتراك جديد.
            </div>
          ) : null}

          {/* ── البطاقة والرصيد الابتدائي */}
          {canReadCards ? (
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">
                  قراءة البطاقة <span className="text-ink-3 font-normal">(اختياري)</span>
                </label>
                <CardReadField
                  value={cardUid}
                  onChange={setCardUid}
                  onResolved={setCard}
                  canIssue={canIssueCard}
                />
              </div>
              {card ? (
                <InitialBalanceField card={card} value={initialBalance} onChange={setInitialBalance} />
              ) : null}
            </div>
          ) : null}

          {/* ── الباقة */}
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">باقة الاشتراك *</label>
            {plans.length === 0 ? (
              <div className="text-[12.5px] text-ink-3 bg-paper-2 rounded-[10px] p-3">
                لا توجد باقات اشتراك فعّالة — أنشئ باقة من تبويب «الباقات الشهرية».
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {plans.map((p) => {
                  const id = String(p._id || p.id);
                  const selected = String(planId) === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setPlanId(id)}
                      className={clsx(
                        'text-right px-3 py-2 rounded-[10px] border transition-colors',
                        selected ? 'bg-orange text-white border-orange shadow-orange-glow' : 'bg-white border-line hover:bg-paper-2'
                      )}
                    >
                      <div className="text-[13px] font-bold">{p.name}</div>
                      <div className={clsx('text-[11.5px]', selected ? 'text-white/85' : 'text-ink-3')}>
                        {durationLabel(p.durationDays)} • {formatIQD(p.price)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة الدفع</label>
            <div className="flex gap-2">
              {METHODS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMethod(m.key)}
                  className={clsx(
                    'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold border transition-colors',
                    method === m.key ? 'bg-orange text-white border-orange' : 'bg-white text-ink-2 border-line hover:bg-paper-2'
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {plan ? (
            <div className="p-3 rounded-[10px] bg-orange-soft text-orange-deep space-y-1">
              <div className="flex items-center justify-between text-[12.5px]">
                <span>الفترة</span>
                <span>
                  {formatDate(new Date())} ← {formatDate(addDays(plan.durationDays))}
                  <Pill variant="orange" className="mr-1.5">{durationLabel(plan.durationDays)}</Pill>
                </span>
              </div>
              <div className="flex items-center justify-between text-[12.5px]">
                <span>قيمة الاشتراك</span>
                <span>{formatIQD(plan.price)}</span>
              </div>
              {card && targetBalance > currentBalance ? (
                <div className="flex items-center justify-between text-[12.5px]">
                  <span>تعبئة رصيد البطاقة</span>
                  <span>{formatIQD(targetBalance - currentBalance)}</span>
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <span className="text-[12.5px]">المبلغ المستلم</span>
                <span className="text-[18px] font-extrabold">
                  {formatIQD(Number(plan.price || 0) + (card ? Math.max(0, targetBalance - currentBalance) : 0))}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>إلغاء</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit}
            onClick={submit}
            title={blockReason}
          >
            <Icon name="check" size={14} /> {submitting ? 'جاري التسجيل...' : 'تأكيد واستلام المبلغ'}
          </button>
        </div>
      </div>
    </div>
  );
}
